import { useMemo, useState, useRef } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription, CardFooter } from '@/components/ui/card';
import { Button } from '@/components/ui/button';

const collectionFilters = [
  { id: 'all', label: 'All Bags' },
  { id: 'jute', label: 'Cotton Pouches' },
  { id: 'cotton', label: 'Cotton Tote Bags' },
  { id: 'canvas', label: 'Jute Bags' },
  { id: 'promotional', label: 'Jute Pouches' },
  { id: 'custom', label: 'Wine Bags Collection' },
];

const ProductImageGallery = ({ images = [], name }) => {
  const [activeIndex, setActiveIndex] = useState(0);
  const hoverTimerRef = useRef(null);
  const touchStartXRef = useRef(null);

  if (!images.length) return null;

  const hasHoverSwap = images.length === 2;
  const primaryImage = images[activeIndex] || images[0];

  const startHoverCycle = () => {
    if (hoverTimerRef.current || images.length <= 1) return;

    hoverTimerRef.current = window.setInterval(() => {
      setActiveIndex((current) => (current + 1) % images.length);
    }, 1000);
  };

  const stopHoverCycle = () => {
    if (hoverTimerRef.current) {
      window.clearInterval(hoverTimerRef.current);
      hoverTimerRef.current = null;
      setActiveIndex(0);
    }
  };

  const handleTouchStart = (event) => {
    const touch = event.touches[0];
    touchStartXRef.current = touch.clientX;
  };

  const handleTouchEnd = (event) => {
    if (touchStartXRef.current == null || images.length <= 1) return;

    const touch = event.changedTouches[0];
    const deltaX = touch.clientX - touchStartXRef.current;
    const threshold = 40;

    if (Math.abs(deltaX) > threshold) {
      setActiveIndex((current) => {
        if (deltaX < 0) {
          // swipe left -> next
          return (current + 1) % images.length;
        }
        // swipe right -> previous
        return (current - 1 + images.length) % images.length;
      });
    }

    touchStartXRef.current = null;
  };

  return (
    <div
      className="relative flex items-center justify-center rounded-xl bg-[#FAFAFA] p-4 overflow-hidden"
      onMouseEnter={startHoverCycle}
      onMouseLeave={stopHoverCycle}
      onTouchStart={handleTouchStart}
      onTouchEnd={handleTouchEnd}
    >
      <div className="relative w-[300px] h-[300px] mx-auto bg-white">
        {hasHoverSwap ? (
          <>
            <img
              src={images[0]}
              alt={name}
              loading="lazy"
              className="absolute inset-0 w-full h-full object-contain object-center transition-opacity duration-500 ease-out opacity-100 group-hover:opacity-0"
            />
            <img
              src={images[1]}
              alt={name}
              loading="lazy"
              className="absolute inset-0 w-full h-full object-contain object-center transition-opacity duration-500 ease-out opacity-0 group-hover:opacity-100"
            />
          </>
        ) : (
          <img
            src={primaryImage}
            alt={name}
            loading="lazy"
            className="w-full h-full object-contain object-center"
          />
        )}
      </div>

      {images.length > 2 && (
        <div className="absolute inset-x-0 bottom-2 flex justify-center gap-1 px-3">
          <div className="inline-flex gap-1 rounded-full bg-white/80 px-2 py-1 max-w-full overflow-x-auto">
            {images.map((imageSrc, index) => (
              <button
                key={imageSrc}
                type="button"
                onClick={() => setActiveIndex(index)}
                className={`h-8 w-8 flex-shrink-0 rounded-md overflow-hidden border ${
                  activeIndex === index ? 'border-[#2F4F3E]' : 'border-transparent'
                }`}
                aria-label={`View ${name} angle ${index + 1}`}
              >
                <img
                  src={imageSrc}
                  alt={name}
                  loading="lazy"
                  className="h-full w-full object-contain"
                />
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

// Base product entries (can contain multiple entries / angles for the same product)
const baseCollectionProducts = [
  {
    id: 'bags4ever-tote',
    productId: 1,
    productName: 'Bags4Ever Sustainable Tote',
    name: 'Bags4Ever Sustainable Tote',
    category: 'canvas',
    material: 'Natural canvas',
    sizes: '38 x 42 cm',
    image: `${process.env.PUBLIC_URL || ''}/3.png`,
    description: 'Light natural tote with Bags4Ever branding, leaf design and QR code. Sustainable by nature.',
    highlights: ['Eco-friendly canvas', 'Custom branding ready', 'Export-quality finish'],
  },
  {
    id: 'cotton-pouch-mountain-thunder',
    productId: 2,
    productName: 'Mountain Thunder Cotton Pouch',
    name: 'Mountain Thunder Cotton Pouch',
    category: 'jute', // mapped to "Cotton Pouches" filter label
    material: '100% cotton',
    sizes: 'Custom coffee pouch size',
    image: `${process.env.PUBLIC_URL || ''}/cotton_pouches/cotton-pouches.png`,
    description: 'Drawstring cotton coffee pouch with bold Mountain Thunder branding and red cord.',
    highlights: ['Soft cotton fabric', 'Premium print quality', 'Ideal for gourmet coffee brands'],
  },
  {
    id: 'cotton-pouch-hualalai',
    productId: 3,
    productName: 'Hualalai Estate Cotton Pouch',
    name: 'Hualalai Estate Cotton Pouch',
    category: 'jute',
    material: 'Cotton canvas',
    sizes: '8 oz coffee pouch',
    images: [
      `${process.env.PUBLIC_URL || ''}/cotton_pouches/cotton-pouches2.png`,
      `${process.env.PUBLIC_URL || ''}/cotton_pouches/cotton-pouches3.png`,
    ],
    description:
      'Front and back views of Hualalai Estate cotton coffee pouch showcasing premium branding and story panel.',
    highlights: ['Export-ready finish', 'Artwork on both sides', 'Reusable packaging'],
  },
  {
    id: 'cotton-pouch-go-green-white',
    productId: 4,
    productName: 'Go Green Cotton Pouch – White',
    name: 'Go Green Cotton Pouch – White',
    category: 'jute',
    material: 'White cotton',
    sizes: 'Phone / accessory pouch',
    image: `${process.env.PUBLIC_URL || ''}/cotton_pouches/cotton-pouches5.png`,
    description: 'Bright white cotton pouch with Go Green graphic and neat drawstring finish.',
    highlights: ['Crisp white base', 'Vibrant print', 'Ideal for gifting and promos'],
  },
  {
    id: 'feel-worthy-tote',
    productId: 5,
    productName: 'Feel Worthy Club Tote',
    name: 'Feel Worthy Club Tote',
    category: 'cotton',
    material: 'Canvas / cotton',
    sizes: '38 x 42 cm',
    image: `${process.env.PUBLIC_URL || ''}/42.png`,
    description: 'Cream canvas tote with bold blue typography. Perfect for lifestyle and wellness brands.',
    highlights: ['Soft-hand fabric', 'Full-face print area', 'Everyday carry'],
  },
  {
    id: 'canvas-magenta-tote',
    productId: 6,
    productName: 'Canvas Tote with Magenta Accents',
    name: 'Canvas Tote with Magenta Accents',
    category: 'canvas',
    material: 'Heavy cotton canvas',
    sizes: '40 x 45 cm',
    image: `${process.env.PUBLIC_URL || ''}/45.png`,
    description: 'Cream canvas tote with vibrant magenta straps, base and branding panel. Structured shape.',
    highlights: ['Contrast trim', 'Custom logo panel', 'Retail-ready'],
  },
  {
    id: 'black-pink-stitch-tote',
    productId: 7,
    productName: 'Black Tote with Pink Stitching',
    name: 'Black Tote with Pink Stitching',
    category: 'canvas',
    material: 'Black canvas with contrast stitch',
    sizes: '38 x 42 cm',
    image: `${process.env.PUBLIC_URL || ''}/34.png`,
    description: 'Black tote with bold fuchsia stitching. Premium finish for fashion and corporate gifting.',
    highlights: ['Contrast stitching', 'Sturdy handles', 'Refined look'],
  },
  {
    id: 'yellow-white-stripe-tote',
    productId: 8,
    productName: 'Yellow & White Striped Tote',
    name: 'Yellow & White Striped Tote',
    category: 'cotton',
    material: 'Canvas',
    sizes: '40 x 44 cm',
    image: `${process.env.PUBLIC_URL || ''}/47.png`,
    description: 'Bright yellow and white vertical stripe tote. Eye-catching for events and retail.',
    highlights: ['Bold stripes', 'High visibility', 'Promotional use'],
  },
  {
    id: 'sirpur-red-tote',
    productId: 9,
    productName: 'Sirpur Branded Tote',
    name: 'Sirpur Branded Tote',
    category: 'cotton',
    material: 'Red canvas',
    sizes: '38 x 42 cm',
    image: `${process.env.PUBLIC_URL || ''}/48.png`,
    description: 'Deep red promotional tote with white logo. Ideal for corporate and event branding.',
    highlights: ['Full branding panel', 'Corporate identity', 'Bulk orders'],
  },
];

// Utility: normalise & smart-group products so that
// different angles of the same product appear in a single card.
const normaliseProductImages = (product) => {
  const images = Array.isArray(product.images)
    ? product.images.filter(Boolean)
    : product.image
    ? [product.image]
    : [];

  return {
    ...product,
    images,
  };
};

const groupCollectionProducts = (products) => {
  const groups = new Map();

  products.forEach((product) => {
    const normalised = normaliseProductImages(product);
    const productId = product.productId || product.id;
    const productName = product.productName || product.name;

    // Group primarily by productId, with productName as a safe fallback
    const groupKey = productId || product.groupKey || productName;

    if (!groups.has(groupKey)) {
      groups.set(groupKey, {
        ...normalised,
        id: groupKey,
        productId,
        productName,
        variants: [
          {
            id: product.variantId || `${groupKey}-variant-1`,
            image: normalised.images[0] || normalised.image || '',
          },
        ],
      });
    } else {
      const existing = groups.get(groupKey);
      const mergedImages = Array.from(new Set([...(existing.images || []), ...normalised.images]));
      const nextVariantIndex = (existing.variants?.length || 0) + 1;
      const variants = [
        ...(existing.variants || []),
        {
          id: product.variantId || `${groupKey}-variant-${nextVariantIndex}`,
          image: normalised.images[0] || normalised.image || '',
        },
      ];

      groups.set(groupKey, {
        ...existing,
        images: mergedImages,
        variants,
      });
    }
  });

  return Array.from(groups.values());
};

// Build products with multiple angles from a flat list of image files.
// Images that belong to the same product are grouped sequentially so that
// each product card can show multiple views (hover / auto-slide).
const buildProductsWithAnglesFromFiles = (fileNames, meta) => {
  const baseUrl = `${process.env.PUBLIC_URL || ''}${meta.basePath}`;

  const items = fileNames
    .map((fileName) => {
      const match = fileName.match(/(\d+)(?=\.[a-z]+$)/i);
      const num = match ? parseInt(match[1], 10) : 0;
      return {
        src: `${baseUrl}/${fileName}`,
        num,
        fileName,
      };
    })
    .sort((a, b) => a.num - b.num || a.fileName.localeCompare(b.fileName));

  const groups = [];
  let currentGroup = [];
  const maxAnglesPerProduct = meta.maxAnglesPerProduct || 3;

  items.forEach((item, index) => {
    currentGroup.push(item.src);
    const next = items[index + 1];
    const hasGapToNext = !next || Math.abs(next.num - item.num) > 1;
    const reachedMaxAngles = currentGroup.length >= maxAnglesPerProduct;

    if (reachedMaxAngles || hasGapToNext) {
      groups.push(currentGroup);
      currentGroup = [];
    }
  });

  if (currentGroup.length) {
    groups.push(currentGroup);
  }

  return groups.map((group, index) => {
    const id = `${meta.idPrefix}-${index + 1}`;

    return {
      id,
      productId: id,
      productName: meta.baseName,
      name: `${meta.baseName} ${index + 1}`,
      category: meta.category,
      material: meta.material,
      sizes: meta.sizes,
      description: meta.description,
      highlights: meta.highlights,
      images: group,
    };
  });
};

const cottonToteFileNames = [
  'cotton-tote-pouches.png',
  'cotton-tote-pouches2.png',
  'cotton-tote-pouches3.png',
  'cotton-tote-pouches4.png',
  'cotton-tote-pouches5.png',
  'cotton-tote-pouches6.png',
  'cotton-tote-pouches7.png',
  'cotton-tote-pouches8.png',
  'cotton-tote-pouches9.png',
  'cotton-tote-pouches10.png',
  'cotton-tote-pouches11.png',
  'cotton-tote-pouches12.png',
  'cotton-tote-pouches13.png',
  'cotton-tote-pouches14.png',
  'cotton-tote-pouches15.png',
  'cotton-tote-pouches16.png',
  'cotton-tote-pouches17.png',
  'cotton-tote-pouches18.png',
  'cotton-tote-pouches19.png',
  'cotton-tote-pouches20.png',
  'cotton-tote-pouches21.png',
  'cotton-tote-pouches22.png',
  'cotton-tote-pouches23.png',
  'cotton-tote-pouches24.png',
  'cotton-tote-pouches25.png',
  'cotton-tote-pouches27.png',
  'cotton-tote-pouches28.png',
  'cotton-tote-pouches29.png',
  'cotton-tote-pouches30.png',
  'cotton-tote-pouches31.png',
  'cotton-tote-pouches32.png',
  'cotton-tote-pouches33.png',
  'cotton-tote-pouches35.png',
  'cotton-tote-pouches36.png',
  'cotton-tote-pouches37.png',
  'cotton-tote-pouches38.png',
];

const autoCottonToteProducts = buildProductsWithAnglesFromFiles(cottonToteFileNames, {
  idPrefix: 'cotton-tote-auto',
  baseName: 'Cotton Tote Bag',
  category: 'cotton',
  material: 'Cotton / canvas',
  sizes: 'Export-ready sizes',
  description: 'Premium cotton tote with multiple angles showing branding and construction details.',
  highlights: ['Multiple views available', 'Export-ready finish', 'Customisable branding'],
  basePath: '/cotton-tote-bags',
  maxAnglesPerProduct: 3,
});

const juteBagFileNames = [
  'jute-bags.png',
  'jute-bags2.png',
  'jute-bags3.png',
  'jute-bags4.png',
  'jute-bags5.png',
  'jute-bags6.png',
  'jute-bags7.png',
  'jute-bags8.png',
  'jute-bags9.png',
  'jute-bags10.png',
  'jute-bags11.png',
  'jute-bags12.png',
  'jute-bags13.png',
  'jute-bags14.png',
  'jute-bags15.png',
  'jute-bags16.png',
  'jute-bags17.png',
  'jute-bags18.png',
  'jute-bags19.png',
  'jute-bags20.png',
  'jute-bags21.png',
  'jute-bags22.png',
  'jute-bags23.png',
  'jute-bags24.png',
  'jute-bags25.png',
  'jute-bags26.png',
  'jute-bags27.png',
  'jute-bags28.png',
  'jute-bags29.png',
  'jute-bags30.png',
  'jute-bags31.png',
  'jute-bags32.png',
  'jute-bags33.png',
  'jute-bags34.png',
  'jute-bags35.png',
  'jute-bags36.png',
  'jute-bags37.png',
  'jute-bags38.png',
  'jute-bags39.png',
  'jute-bags40.png',
  'jute-bags41.png',
  'jute-bags42.png',
  'jute-bags43.png',
  'jute-bags44.png',
  'jute-bags45.png',
  'jute-bags46.png',
  'jute-bags47.png',
  'jute-bags48.png',
  'jute-bags49.png',
  'jute-bags50.png',
  'jute-bags51.png',
  'jute-bags52.png',
  'jute-bags53.png',
  'jute-bags54.png',
  'jute-bags55.png',
  'jute-bags56.png',
  'jute-bags57.png',
  'jute-bags58.png',
  'jute-bags59.png',
  'jute-bags60.png',
  'jute-bags61.png',
  'jute-bags62.png',
  'jute-bags63.png',
  'jute-bags64.png',
  'jute-bags65.png',
  'jute-bags66.png',
  'jute-bags67.png',
  'jute-bags68.png',
  'jute-bags69.png',
  'jute-bags70.png',
  'jute-bags71.png',
  'rope_copy-removebg-preview.png',
];

const autoJuteBagProducts = buildProductsWithAnglesFromFiles(juteBagFileNames, {
  idPrefix: 'jute-bag-auto',
  baseName: 'Jute Bag',
  category: 'canvas',
  material: 'Natural jute',
  sizes: 'Export-ready sizes',
  description: 'Premium jute shoppers and totes with multiple views showing gussets, branding and capacity.',
  highlights: ['Multiple product angles', 'Strong natural fibre construction', 'Custom branding options'],
  basePath: '/jute-bags',
  maxAnglesPerProduct: 3,
});

// Manually model Jute Pouches so that:
// - all images for the same product appear inside a single card
// - different products continue to render as separate cards
const jutePouchProducts = [
  {
    id: 'jute-pouch-coffee-print-1',
    productId: 'jute-pouch-coffee-print-1',
    productName: 'Coffee Print Jute Pouch – Ground',
    name: 'Coffee Print Jute Pouch – Ground',
    category: 'promotional',
    material: 'Natural jute',
    sizes: 'Custom coffee pouch size',
    description: 'Drawstring jute coffee pouch with bold front branding and contrast red cord.',
    highlights: ['Natural jute fabric', 'Bold coffee branding', 'Ideal for gourmet coffee packs'],
    images: [
      `${process.env.PUBLIC_URL || ''}/jute-pouches/24-removebg-preview.png`,
    ],
  },
  {
    id: 'jute-pouch-coffee-print-2',
    productId: 'jute-pouch-coffee-print-2',
    productName: 'Coffee Print Jute Pouch – Bean',
    name: 'Coffee Print Jute Pouch – Bean',
    category: 'promotional',
    material: 'Natural jute',
    sizes: 'Custom coffee pouch size',
    description: 'Drawstring jute coffee pouch with bold front branding and contrast purple cord.',
    highlights: ['Natural jute fabric', 'Premium bean branding', 'Perfect for speciality coffee lines'],
    images: [
      `${process.env.PUBLIC_URL || ''}/jute-pouches/25-removebg-preview.png`,
      `${process.env.PUBLIC_URL || ''}/jute-pouches/24-removebg-preview.png`,
    ],
  },
  {
    id: 'jute-pouch-go-green-standard',
    productId: 'jute-pouch-go-green-standard',
    productName: 'Go Green Jute Pouch',
    name: 'Go Green Jute Pouch',
    category: 'promotional',
    material: 'Natural jute',
    sizes: 'Phone / accessory pouch',
    description: 'Natural jute pouch with Go Green graphic and drawstring closure.',
    highlights: ['Eco-friendly jute base', 'Vibrant Go Green artwork', 'Reusable gift packaging'],
    images: [
      `${process.env.PUBLIC_URL || ''}/jute-pouches/27-removebg-preview.png`,
      `${process.env.PUBLIC_URL || ''}/jute-pouches/27_A-removebg-preview.png`,
    ],
  },
  {
    id: 'jute-pouch-go-green-variants',
    productId: 'jute-pouch-go-green-variants',
    productName: 'Go Green Jute Pouch – Variants',
    name: 'Go Green Jute Pouch – Variants',
    category: 'promotional',
    material: 'Natural & dyed jute',
    sizes: 'Custom pouch sizes',
    description: 'Assorted Go Green jute pouches in different shades and sizes for varied branding needs.',
    highlights: ['Multiple colourways', 'Eye-catching eco branding', 'Suitable for mixed campaigns'],
    images: [
      `${process.env.PUBLIC_URL || ''}/jute-pouches/42-removebg-preview.png`,
      `${process.env.PUBLIC_URL || ''}/jute-pouches/44-removebg-preview.png`,
      `${process.env.PUBLIC_URL || ''}/jute-pouches/58-removebg-preview.png`,
      `${process.env.PUBLIC_URL || ''}/jute-pouches/102-removebg-preview.png`,
    ],
  },
  {
    id: 'jute-pouch-borges',
    productId: 'jute-pouch-borges',
    productName: 'Borges Jute Pouch',
    name: 'Borges Jute Pouch',
    category: 'promotional',
    material: 'Natural jute',
    sizes: 'Dry fruit / nut pouch size',
    description: 'Branded jute pouch with Borges logo, ideal for dry fruits and gourmet packs.',
    highlights: ['Branded for retail', 'Natural fibre look', 'Durable construction'],
    images: [
      `${process.env.PUBLIC_URL || ''}/jute-pouches/Borges_Jute_Pouch-removebg-preview.png`,
    ],
  },
  {
    id: 'jute-pouch-colored-set',
    productId: 'jute-pouch-colored-set',
    productName: 'Coloured Jute Pouch Set',
    name: 'Coloured Jute Pouch Set',
    category: 'promotional',
    material: 'Dyed jute',
    sizes: 'Small gift pouch sizes',
    description: 'Set of assorted coloured jute pouches perfect for gifting and promotional hampers.',
    highlights: ['Multiple colour options', 'Compact gift size', 'Ideal for hampers and favours'],
    images: [
      `${process.env.PUBLIC_URL || ''}/jute-pouches/JP003_Colored_pouches-removebg-preview.png`,
    ],
  },
  {
    id: 'jute-pouch-coffee-beans-print-1',
    productId: 'jute-pouch-coffee-beans-print-1',
    productName: 'Coffee Beans Jute Pouch',
    name: 'Coffee Beans Jute Pouch',
    category: 'promotional',
    material: 'Natural jute',
    sizes: 'Bottle / tall pouch size',
    description: 'Tall jute pouch with repeating coffee bean print and drawstring closure.',
    highlights: ['Coffee bean all-over print', 'Tall profile', 'Great for bottles or long packs'],
    images: [
      `${process.env.PUBLIC_URL || ''}/jute-pouches/JP006-removebg-preview.png`,
      `${process.env.PUBLIC_URL || ''}/jute-pouches/JP007-removebg-preview.png`,
    ],
  },
];

// Wine bag products with multiple angles grouped into a single card
const wineBagProducts = [
  {
    id: 'wine-bag-bg-07',
    productId: 'wine-bag-bg-07',
    productName: 'Wine Bag – BG 07',
    name: 'Wine Bag – BG 07',
    category: 'custom', // mapped to "Wine Bags Collection" filter
    material: 'Natural jute',
    sizes: '2 bottle wine bag',
    description: 'Natural jute wine bag with printed glass motif and sturdy carry handles, designed to hold two bottles.',
    highlights: ['Holds two wine bottles', 'Strong jute construction', 'Attractive glass print'],
    images: [
      `${process.env.PUBLIC_URL || ''}/wine-bags/BG - 07 (Front A).png`,
      `${process.env.PUBLIC_URL || ''}/wine-bags/BG - 07 (Front B).png`,
      `${process.env.PUBLIC_URL || ''}/wine-bags/BG - 07 (Side).png`,
      `${process.env.PUBLIC_URL || ''}/wine-bags/BG - 07 (Top).png`,
    ],
  },
  {
    id: 'wine-bag-bg-17',
    productId: 'wine-bag-bg-17',
    productName: 'Wine Bag – BG 17',
    name: 'Wine Bag – BG 17',
    category: 'custom', // mapped to "Wine Bags Collection" filter
    material: 'Natural jute with transparent front panel',
    sizes: '2 bottle wine bag',
    description:
      'Two-bottle wine bag with clear front panel and contrast black jute body, ideal for premium gifting.',
    highlights: ['Transparent display front', 'Contrast black jute body', 'Strong carry handles'],
    images: [
      `${process.env.PUBLIC_URL || ''}/wine-bags/BG - 17 (Front A).png`,
      `${process.env.PUBLIC_URL || ''}/wine-bags/BG - 17 (Front B).png`,
      `${process.env.PUBLIC_URL || ''}/wine-bags/BG - 17 (Side A).png`,
      `${process.env.PUBLIC_URL || ''}/wine-bags/BG - 17 (Side B).png`,
    ],
  },
  {
    id: 'wine-bag-fatherhood',
    productId: 'wine-bag-fatherhood',
    productName: 'Fatherhood Wine Bag',
    name: 'Fatherhood Wine Bag',
    category: 'custom', // mapped to "Wine Bags Collection" filter
    material: 'Natural jute with contrast border',
    sizes: 'Single bottle wine bag',
    description: 'Single-bottle jute wine bag with Fatherhood Wine artwork and contrast maroon border.',
    highlights: ['Single bottle size', 'Printed Fatherhood Wine artwork', 'Strong rope handles'],
    images: [
      `${process.env.PUBLIC_URL || ''}/wine-bags/F - 6.png`,
      `${process.env.PUBLIC_URL || ''}/wine-bags/F - 7.png`,
    ],
  },
  {
    id: 'wine-bag-bg-20',
    productId: 'wine-bag-bg-20',
    productName: 'Wine Bag – BG 20',
    name: 'Wine Bag – BG 20',
    category: 'custom', // mapped to "Wine Bags Collection" filter
    material: 'Natural jute with clear front panel',
    sizes: '2 bottle wine bag',
    description:
      'Natural jute wine bag with glass motif print and clear window, designed to securely hold two bottles.',
    highlights: ['Clear front window', 'Secure two-bottle divider', 'Durable jute construction'],
    images: [
      `${process.env.PUBLIC_URL || ''}/wine-bags/BG - 20 (Front).png`,
      `${process.env.PUBLIC_URL || ''}/wine-bags/BG - 20 (Back).png`,
      `${process.env.PUBLIC_URL || ''}/wine-bags/BG - 20 (Side A).png`,
      `${process.env.PUBLIC_URL || ''}/wine-bags/BG - 20 (Side B).png`,
      `${process.env.PUBLIC_URL || ''}/wine-bags/BG - 20 (Top).png`,
    ],
  },
  {
    id: 'wine-bag-4-bottle',
    productId: 'wine-bag-4-bottle',
    productName: '4 Bottle Wine Bag',
    name: '4 Bottle Wine Bag',
    category: 'custom', // mapped to "Wine Bags Collection" filter
    material: 'Natural jute',
    sizes: '4 bottle wine bag',
    description: 'Sturdy natural jute wine bag sized to hold four bottles, with strong rope handles.',
    highlights: ['Holds four bottles', 'Strong rope handles', 'Durable jute construction'],
    images: [
      `${process.env.PUBLIC_URL || ''}/wine-bags/4 Bottle Wine Bag 3.png`,
      `${process.env.PUBLIC_URL || ''}/wine-bags/4 Bottle Wine Bag 4.png`,
      `${process.env.PUBLIC_URL || ''}/wine-bags/4 Bottle Wine Bag 5.png`,
    ],
  },
  {
    id: 'wine-bag-bg-06-b',
    productId: 'wine-bag-bg-06-b',
    productName: 'Wine Bag – BG 06B',
    name: 'Wine Bag – BG 06B',
    category: 'custom', // mapped to "Wine Bags Collection" filter
    material: 'Natural jute',
    sizes: 'Single bottle wine bag',
    description: 'Single-bottle wine bag with white glass print and natural jute body.',
    highlights: ['Single bottle size', 'White glass print', 'Strong rope handles'],
    images: [
      `${process.env.PUBLIC_URL || ''}/wine-bags/BG - 06 B (Front).png`,
      `${process.env.PUBLIC_URL || ''}/wine-bags/BG - 06 B (Side).png`,
    ],
  },
  {
    id: 'wine-bag-bg-8-b',
    productId: 'wine-bag-bg-8-b',
    productName: 'Wine Bag – BG 8B',
    name: 'Wine Bag – BG 8B',
    category: 'custom', // mapped to "Wine Bags Collection" filter
    material: 'Dyed jute',
    sizes: 'Single bottle wine bag',
    description: 'Single-bottle dyed jute wine bag with white glass print and natural rope handles.',
    highlights: ['Single bottle size', 'Dyed jute body', 'White glass print'],
    images: [
      `${process.env.PUBLIC_URL || ''}/wine-bags/BG - 8 B (Front 1).png`,
      `${process.env.PUBLIC_URL || ''}/wine-bags/BG - 8 B (Front 2).png`,
      `${process.env.PUBLIC_URL || ''}/wine-bags/BG - 8 B (Side).png`,
    ],
  },
  {
    id: 'wine-bag-bg-10-d',
    productId: 'wine-bag-bg-10-d',
    productName: 'Wine Bag – BG 10D',
    name: 'Wine Bag – BG 10D',
    category: 'custom', // mapped to "Wine Bags Collection" filter
    material: 'Natural jute with printed panel',
    sizes: 'Single bottle wine bag',
    description: 'Slim single-bottle wine bag with decorative grape vine print and contrast black sides.',
    highlights: ['Slim profile', 'Grape vine artwork', 'Contrast black side panels'],
    images: [
      `${process.env.PUBLIC_URL || ''}/wine-bags/BG - 10 D (Front).png`,
      `${process.env.PUBLIC_URL || ''}/wine-bags/BG - 10 D (Side).png`,
    ],
  },
  {
    id: 'wine-bag-bg-10-f',
    productId: 'wine-bag-bg-10-f',
    productName: 'Wine Bag – BG 10F',
    name: 'Wine Bag – BG 10F',
    category: 'custom', // mapped to "Wine Bags Collection" filter
    material: 'Natural jute with printed panel',
    sizes: 'Single bottle wine bag',
    description: 'Slim single-bottle wine bag with pink grape vine print and natural jute body.',
    highlights: ['Slim profile', 'Pink grape vine artwork', 'Strong rope handles'],
    images: [
      `${process.env.PUBLIC_URL || ''}/wine-bags/BG - 10 F (Front).png`,
      `${process.env.PUBLIC_URL || ''}/wine-bags/BG - 10 F (Side).png`,
    ],
  },
  {
    id: 'wine-bag-bg-19',
    productId: 'wine-bag-bg-19',
    productName: 'Wine Bag – BG 19',
    name: 'Wine Bag – BG 19',
    category: 'custom', // mapped to "Wine Bags Collection" filter
    material: 'Natural jute with clear front panel',
    sizes: '2 bottle wine bag',
    description: 'Two-bottle natural jute wine bag with clear front window and sturdy rope handles.',
    highlights: ['Clear display window', 'Holds two bottles', 'Durable jute construction'],
    images: [
      `${process.env.PUBLIC_URL || ''}/wine-bags/BG - 19 (Front).png`,
      `${process.env.PUBLIC_URL || ''}/wine-bags/BG - 19 (Side A).png`,
    ],
  },
];

const collectionProducts = [
  ...baseCollectionProducts,
  ...jutePouchProducts,
  ...wineBagProducts,
  ...autoCottonToteProducts,
  ...autoJuteBagProducts,
];

export const CollectionPage = ({ onRequestQuote }) => {
  const [activeFilter, setActiveFilter] = useState('all');
  const [expandedProductId, setExpandedProductId] = useState(null);

  const filteredProducts = useMemo(() => {
    const baseList =
      activeFilter === 'all'
        ? collectionProducts
        : collectionProducts.filter((product) => product.category === activeFilter);

    // Smart grouping applied on a per-category basis
    return groupCollectionProducts(baseList);
  }, [activeFilter]);

  const handleViewDetails = (productId) => {
    setExpandedProductId((current) => (current === productId ? null : productId));
  };

  return (
    <div className="bg-[#F5F2ED] min-h-screen pb-16">
      {/* Hero / Banner */}
      <section
        id="home"
        className="pt-24 md:pt-28 pb-10 md:pb-12 leaf-pattern"
        data-testid="collection-hero"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className=" text-xs tracking-[0.25em] uppercase text-[#5A5A5A] mb-3">
            Premium Eco Bag Collection
          </p>
          <h1 className=" text-3xl sm:text-4xl lg:text-5xl text-[#2B2B2B] mb-4">
            Product List
          </h1>
          <p className=" text-[#5A5A5A] text-sm sm:text-base max-w-2xl">
            Discover export-quality jute, cotton and canvas bags crafted for global retailers,
            promotional agencies and brands that demand sustainable, long-lasting packaging.
          </p>
        </div>
      </section>

      {/* Filters */}
      <section className="bg-[#F5F2ED] pb-4" aria-label="Filter collection by category">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-wrap gap-2">
            {collectionFilters.map((filter) => (
              <Button
                key={filter.id}
                type="button"
                variant={activeFilter === filter.id ? 'default' : 'outline'}
                size="sm"
                onClick={() => setActiveFilter(filter.id)}
                className={
                  activeFilter === filter.id
                    ? 'bg-[#2F4F3E] hover:bg-[#233B2F] text-white  text-xs sm:text-sm'
                    : ' text-xs sm:text-sm'
                }
              >
                {filter.label}
              </Button>
            ))}
          </div>
        </div>
      </section>

      {/* Product Grid */}
      <section
        id="collections"
        className="py-8 md:py-12"
        data-testid="collection-grid-section"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
            {filteredProducts.map((product) => {
              const isExpanded = expandedProductId === product.id;
              const images =
                Array.isArray(product.images) && product.images.length > 0
                  ? product.images
                  : product.image
                  ? [product.image]
                  : [];

              return (
                <Card
                  key={product.id}
                  className="group flex flex-col h-full overflow-hidden border-[#E3DED8] bg-white/90 hover:bg-white transition-all duration-300 hover:-translate-y-1 hover:shadow-lg"
                >
                  <ProductImageGallery images={images} name={product.name} />
                  <CardHeader className="pb-3">
                    <CardTitle className=" text-lg text-[#2B2B2B] mb-1">
                      {product.name}
                    </CardTitle>
                    <CardDescription className=" text-xs sm:text-sm text-[#5A5A5A]">
                      {product.description}
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="pt-0 pb-3">
                    <dl className="space-y-1.5 text-xs sm:text-sm  text-[#2B2B2B]">
                      <div className="flex items-start justify-between gap-3">
                        <dt className="text-[#5A5A5A]">Material</dt>
                        <dd className="text-right">{product.material}</dd>
                      </div>
                      <div className="flex items-start justify-between gap-3">
                        <dt className="text-[#5A5A5A]">Available sizes</dt>
                        <dd className="text-right">{product.sizes}</dd>
                      </div>
                    </dl>
                    {isExpanded && product.highlights && (
                      <ul className="mt-3 space-y-1.5 text-xs sm:text-sm  text-[#5A5A5A]">
                        {product.highlights.map((item) => (
                          <li key={item} className="list-disc list-inside">
                            {item}
                          </li>
                        ))}
                      </ul>
                    )}
                  </CardContent>
                  <CardFooter className="mt-auto flex flex-col gap-2 pt-0">
                    <div className="flex gap-2 w-full">
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        className="flex-1  text-xs sm:text-sm"
                        onClick={() => handleViewDetails(product.id)}
                      >
                        {isExpanded ? 'Hide Details' : 'View Details'}
                      </Button>
                      <Button
                        type="button"
                        size="sm"
                        className="flex-1 bg-[#2F4F3E] hover:bg-[#233B2F] text-white  text-xs sm:text-sm"
                        onClick={onRequestQuote}
                      >
                        Enquire Now
                      </Button>
                    </div>
                  </CardFooter>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Manufacturing quality & export standards */}
      <section
        id="wholesale"
        className="py-16 md:py-20 bg-[#F5F2ED] leaf-pattern border-t border-[#E3DED8]"
        data-testid="collection-quality-section"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div
            id="sustainability"
            className="max-w-3xl mx-auto text-center"
          >
            <h2 className=" text-2xl sm:text-3xl lg:text-4xl text-[#2B2B2B] mb-4">
              Manufacturing Quality & Export Standards
            </h2>
            <p className=" text-sm sm:text-base text-[#5A5A5A] mb-6">
              Bags4Ever partners with audited facilities to deliver export-ready bags that meet
              demanding retail and promotional standards across Europe, the Middle East, Asia and
              the Americas.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8 max-w-4xl mx-auto mb-8">
            <div className="bg-white/80 rounded-lg border border-[#E3DED8] p-5 text-left">
              <h3 className=" text-base text-[#2B2B2B] mb-2">
                Consistent Export Quality
              </h3>
              <p className=" text-sm text-[#5A5A5A]">
                Fabric, stitching and finishing are controlled through AQL-based inspections and
                documented quality gates from raw material to final packing.
              </p>
            </div>
            <div className="bg-white/80 rounded-lg border border-[#E3DED8] p-5 text-left">
              <h3 className=" text-base text-[#2B2B2B] mb-2">
                Responsible & Eco-Friendly
              </h3>
              <p className=" text-sm text-[#5A5A5A]">
                We prioritise organic cotton, natural jute, recycled fibers and low-impact inks to
                support your sustainability commitments without compromising performance.
              </p>
            </div>
            <div className="bg-white/80 rounded-lg border border-[#E3DED8] p-5 text-left">
              <h3 className=" text-base text-[#2B2B2B] mb-2">
                Built for Scale
              </h3>
              <p className=" text-sm text-[#5A5A5A]">
                Flexible MOQs, rapid sampling and coordinated production across partner units allow
                us to support roll-outs from a few hundred pieces to high-volume export programs.
              </p>
            </div>
          </div>

          <div className="text-center">
            <Button
              type="button"
              onClick={onRequestQuote}
              className="bg-[#2F4F3E] hover:bg-[#233B2F] text-white rounded-md px-8 py-4 text-sm font-medium"
            >
              Discuss Bulk Manufacturing
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
};
